import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Dayjs } from 'dayjs';

/** Quick jumps offered next to the field — the common cases when scoping a day. */
const SHORTCUTS: { label: string; hour: number; minute: number }[] = [
  { label: '00:00', hour: 0, minute: 0 },
  { label: '12:00', hour: 12, minute: 0 },
  { label: '23:59', hour: 23, minute: 59 },
];

/**
 * Time half of a {@link CalendarComponent} column.
 *
 * Replaces the old dual hour/minute range sliders: dragging a 0–59 slider to an
 * exact minute is nearly impossible, and the sliders had no dark mode. A native
 * `input[type=time]` is keyboard- and locale-aware, and `color-scheme` makes the
 * browser paint its own picker in the right theme.
 */
@Component({
  selector: 'lib-time-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <div class="flex items-center justify-center gap-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-slate-900/60">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="h-4 w-4 shrink-0 text-gray-400">
        <circle cx="12" cy="12" r="9" />
        <path stroke-linecap="round" d="M12 7v5l3 2" />
      </svg>
      <input
        type="time"
        step="60"
        [value]="timeValue()"
        (change)="onInput($event)"
        class="focus:border-primary-500 focus:ring-primary-500 w-[104px] rounded-md border border-gray-300 bg-white px-2 py-1 text-center font-mono text-sm text-gray-900 [color-scheme:light] focus:outline-none focus:ring-1 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:[color-scheme:dark]" />
      <div class="flex gap-1">
        @for (s of shortcuts; track s.label) {
          <button
            type="button"
            class="rounded px-1.5 py-1 text-[11px] font-medium text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
            (click)="valueChange.emit(value().hour(s.hour).minute(s.minute))">
            {{ s.label }}
          </button>
        }
      </div>
    </div>
  `,
})
export class TimeFieldComponent {
  readonly value = input.required<Dayjs>();
  readonly valueChange = output<Dayjs>();

  protected readonly shortcuts = SHORTCUTS;
  protected readonly timeValue = computed(() => this.value().format('HH:mm'));

  protected onInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    // Clearing the field yields '' — ignore it and keep the current time.
    if (!raw) return;
    const [h, m] = raw.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return;
    this.valueChange.emit(this.value().hour(h).minute(m));
  }
}
